import React, { useContext } from 'react';
import { GeneralImage, MetadataContext, ModuleSection, Stack } from '@/components';
import { ImageModel, LinkModel, ModuleBaseModel, ModuleSectionPropsModel } from '@/types';
import classNames from 'classnames';
import { animControllerInView, fadeIn } from '@/animations';

enum ImageOrientation {
  Portrait = 'Portrait',
  Landscape = 'Landscape',
  Video = '16:9',
  Square = 'Square',
}

type ImagesRowModuleModel = ModuleBaseModel<
  {
    anchor: string;
    imagesInRow: 2 | 3 | 4;
    imageOrientation: ImageOrientation;
    images: ImageModel[];
    noPaddingOnTop: boolean;
    noPaddingOnBottom: boolean;
    imageLinks: LinkModel[];
    fitToPageWidth: boolean;
  } & ModuleSectionPropsModel
>;

const ImagesRowModule = (data: ImagesRowModuleModel) => {
  const {
    props: { images, imagesInRow, imageOrientation, fitToPageWidth, imageLinks },
  } = data;

  const { pageType } = useContext(MetadataContext);

  const generalImageClasses: string[] = [imagesInRow === 2 ? 'sm:w-1/2' : imagesInRow === 3 ? 'sm:w-1/3' : 'sm:w-1/4'];
  if (imageOrientation === ImageOrientation.Landscape) {
    generalImageClasses.push('aspect-[4/3] [&_.general-image]:w-full');
  } else if (imageOrientation === ImageOrientation.Portrait) {
    generalImageClasses.push('aspect-[3/4]');
  } else if (imageOrientation === ImageOrientation.Video) {
    generalImageClasses.push('aspect-video [&_.general-image]:w-full');
  } else {
    generalImageClasses.push('aspect-square');
  }

  const { widths, heights } = generalImageOptions(imagesInRow, imageOrientation);

  return (
    <ModuleSection
      data={data}
      sectionHeadingProps={{
        className: classNames(
          'mx-auto flex w-full flex-col justify-center  lg:px-6 pb-8 md:pb-16 [&_.summary]:pt-5',
          !fitToPageWidth && 'lg:w-3/4',
        ),
      }}
    >
      <div className={classNames('container lg:mx-auto', fitToPageWidth && '!px-1.5')}>
        <Stack
          animation={animControllerInView({ amount: 'some', margin: '-20%' })}
          className={classNames(
            'ImageContainer mx-auto flex w-full flex-wrap justify-center',
            !fitToPageWidth && 'lg:w-3/4',
          )}
        >
          {images.map((image, i) => {
            const targetImageUrl = imageLinks.length >= i + 1 ? imageLinks[i] : undefined;
            return (
              <GeneralImage
                key={image.mediaId}
                image={image}
                animation={fadeIn}
                widths={widths}
                heights={heights}
                disableClick={pageType === 'landingPage' && !targetImageUrl}
                className={classNames(
                  'w-1/2 [&_.caption]:pb-1.5 [&_.caption]:pl-3.5 [&_.general-image]:p-1.5',
                  generalImageClasses,
                )}
                targetImageUrl={targetImageUrl}
              />
            );
          })}
        </Stack>
      </div>
    </ModuleSection>
  );
};

const generalImageOptions = (imagesInRow: 2 | 3 | 4, imageOrientation: ImageOrientation) => {
  // multiplier
  const m = 4 / (imagesInRow || 4);

  if (imageOrientation === ImageOrientation.Portrait) {
    // portrait should have ratio 3:4 => 0.75
    return {
      widths: {
        xs: 395,
        sm: 221 * m,
        md: 276 * m,
        lg: 278 * m,
        xl: 422 * m,
      },
      heights: {
        xs: 526,
        sm: 294.6 * m,
        md: 368 * m,
        lg: 370.6 * m,
        xl: 562.6666666666667 * m,
      },
    };
  } else if (imageOrientation === ImageOrientation.Landscape) {
    // landscape should have ratio 4:3 => 1.33(3)
    return {
      widths: {
        xs: 388,
        sm: 180 * m,
        md: 220 * m,
        lg: 280 * m,
        xl: 430 * m,
      },
      heights: {
        xs: 291,
        sm: 135 * m,
        md: 165 * m,
        lg: 210 * m,
        xl: 322.5 * m,
      },
    };
  } else if (imageOrientation === ImageOrientation.Video) {
    const rv = {
      widths: {
        xs: 388,
        sm: 180 * m,
        md: 220 * m,
        lg: 280 * m,
        xl: 430 * m,
      },
      heights: {
        xs: 218,
        sm: 76 * m,
        md: 124 * m,
        lg: 157.5 * m,
        xl: 242 * m,
      },
    };

    return rv;
  }
  // default to 1:1
  return {
    widths: {
      xs: 412,
      sm: 180 * m,
      md: 220 * m,
      lg: 290 * m,
      xl: 350 * m,
    },
    heights: {
      xs: 412,
      sm: 180 * m,
      md: 220 * m,
      lg: 290 * m,
      xl: 350 * m,
    },
  };
};

export default ImagesRowModule;
